<template>
  <nav class="pagination" role="navigation">
    <span
      class="visually-hidden"
      v-text="$string('general.pagination.label')"
    />

    <ul class="pagination__list">
      <li v-if="currentPage > 1" class="pagination__list-item">
        <a
          class="pagination__button pagination__part"
          :href="getPaginationLink(currentPage - 1)"
          @click.prevent="$emit('go-to-page', currentPage - 1)"
        >
          <span
            class="visually-hidden"
            v-text="$string('general.pagination.previous')"
          />
          <icon-arrow-left />
        </a>
      </li>

      <li
        v-for="(page, idx) of displayPages"
        :key="`page-${idx}-${page}`"
        class="pagination__list-item"
      >
        <a
          v-if="typeof page === 'number' && page !== currentPage"
          class="pagination__link pagination__part text-body-m-regular"
          :href="getPageUrl(page)"
          @click.prevent="$emit('go-to-page', page)"
        >
          <span
            class="visually-hidden"
            v-text="$string('general.pagination.page', { page })"
          />
          {{ page }}
        </a>
        <span
          v-else-if="typeof page === 'number' && page === currentPage"
          :class="getPageClass(page)"
          aria-current="page"
          data-active="true"
        >
          <span
            class="visually-hidden"
            v-text="$string('general.pagination.page', { page })"
          />
          {{ page }}
        </span>
        <span v-else class="pagination__ellipsis" aria-hidden="true">
          {{ page }}
        </span>
      </li>

      <li v-if="currentPage < totalPages" class="pagination__list-item">
        <a
          class="pagination__button pagination__part"
          :href="getPaginationLink(currentPage + 1)"
          @click.prevent="$emit('go-to-page', currentPage + 1)"
        >
          <span
            class="visually-hidden"
            v-text="$string('general.pagination.next')"
          />
          <icon-arrow-right />
        </a>
      </li>
    </ul>
  </nav>
</template>

<script>
/**
 * Vue: pagination (pagination)
 * -----------------------------------------------------------------------------
 * Pagination for grids that load pages async
 * @param {Number} [totalPages] - Total amount of pages.
 * @param {Number} [currentPage] - currently selected page
 *
 */

import IconArrowLeft from '~icons/directional-navigation/arrow/left.svg';
import IconArrowRight from '~icons/directional-navigation/arrow/right.svg';

export default {
  // eslint-disable-next-line vue/multi-word-component-names
  name: 'Pagination',

  components: {
    IconArrowLeft,
    IconArrowRight,
  },

  props: {
    totalPages: {
      type: Number,
      default: 0,
    },
    currentPage: { type: Number, default: -1 },
    pageUrl: {
      type: String,
      default: '',
    },
  },

  emits: {
    'go-to-page': (payload) => typeof payload === 'number',
  },

  computed: {
    displayPages() {
      const total = Math.max(this.totalPages, 1);
      const current = Math.min(Math.max(this.currentPage, 1), total);

      const pages = [];
      const startWindow = Math.max(2, current - 2);
      const endWindow = Math.min(total - 1, current + 2);

      pages.push(1);

      if (startWindow > 2) pages.push('…');

      for (let pageIdx = startWindow; pageIdx <= endWindow; pageIdx++) {
        pages.push(pageIdx);
      }

      if (endWindow < total - 1) pages.push('…');
      if (total > 1) pages.push(total);

      return pages;
    },
  },

  methods: {
    getPaginationLink(page) {
      if (this.pageUrl === '') {
        return '';
      }
      const url = new URL(this.pageUrl);
      // clear only our own URL parameters
      url.searchParams.delete('page');

      if (page > 1) {
        url.searchParams.set('page', page);
      }
      return url;
    },

    /**
     * Get page class.
     * @param {Number} page - Page number.
     * @returns {String}
     */
    getPageClass(page) {
      return page === this.currentPage
        ? 'pagination__part text-body-m-regular is-active'
        : 'pagination__part text-body-m-regular';
    },

    getPageUrl(page) {
      const url = new URL(window.location.href);
      url.searchParams.delete('page');

      if (page > 1) {
        url.searchParams.set('page', page);
      }
      return url;
    },
  },
};
</script>

<style lang="scss">
@import '@/components/pagination';
</style>
